import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { FileText, X, RefreshCw, Loader2 } from 'lucide-react';

const ContractPreviewModal = ({ show, onClose, contractData }) => {
    const [pdfUrl, setPdfUrl] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const mountedRef = useRef(true);

    // Generate PDF when modal opens
    useEffect(() => {
        mountedRef.current = true;

        if (show && contractData?.content) {
            generatePdf();
        }

        return () => {
            mountedRef.current = false;
            // Clean up blob URL
            if (pdfUrl) {
                URL.revokeObjectURL(pdfUrl);
            }
        };
    }, [show, contractData?.id]);

    const generatePdf = async () => {
        if (!contractData?.content) {
            setError('Kein Vertragsinhalt vorhanden');
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            const response = await fetch('/index.php/contracts/previewPDF', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    content: contractData.content,
                    type: contractData.type || contractData.art || 1
                })
            });

            if (!response.ok) {
                throw new Error('PDF-Generierung fehlgeschlagen');
            }

            const contentType = response.headers.get('content-type');
            if (contentType && contentType.includes('application/json')) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Unbekannter Fehler');
            }

            const blob = await response.blob();

            // Clean up old URL
            if (pdfUrl) {
                URL.revokeObjectURL(pdfUrl);
            }

            const newUrl = URL.createObjectURL(blob);
            if (mountedRef.current) {
                setPdfUrl(newUrl);
            }
        } catch (err) {
            console.error('PDF preview error:', err);
            if (mountedRef.current) {
                setError(err.message);
            }
        } finally {
            if (mountedRef.current) {
                setIsLoading(false);
            }
        }
    };

    // Clean up when modal closes
    const handleClose = () => {
        if (pdfUrl) {
            URL.revokeObjectURL(pdfUrl);
            setPdfUrl(null);
        }
        setError(null);
        onClose();
    };

    return (
        <Modal show={show} onHide={handleClose} centered size="xl">
            <Modal.Header closeButton style={{ borderBottom: '1px solid var(--border-light, #e2e8f0)' }}>
                <Modal.Title style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    fontSize: '1.125rem',
                    fontWeight: 600,
                    color: 'var(--text-primary, #2c3e50)'
                }}>
                    <FileText size={24} style={{ color: 'var(--accent-slate, #5b7c8d)' }} />
                    PDF-Vorschau: {contractData?.title || 'Vertrag'}
                </Modal.Title>
            </Modal.Header>

            <Modal.Body style={{ padding: '0', minHeight: '70vh', display: 'flex', flexDirection: 'column' }}>
                {/* Contract Info Bar */}
                {contractData && (
                    <div style={{
                        padding: '0.75rem 1.5rem',
                        background: 'var(--bg-secondary, #f5f7f9)',
                        borderBottom: '1px solid var(--border-light, #e2e8f0)',
                        fontSize: '0.8125rem',
                        color: 'var(--text-secondary, #64748b)',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: '1rem'
                    }}>
                        <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
                            <span>
                                <strong>Kategorie:</strong>{' '}
                                {contractData.type_label ||
                                    (contractData.art === 1 || contractData.type === 1 ? 'Jobs' :
                                     contractData.art === 2 || contractData.type === 2 ? 'Hochzeiten' : 'Allgemein')}
                            </span>
                            {contractData.cancellation_policy_obligation === 1 && (
                                <span style={{ color: '#ef4444' }}>
                                    <strong>Widerrufserklärung:</strong> Erforderlich
                                </span>
                            )}
                        </div>
                        <button
                            onClick={generatePdf}
                            disabled={isLoading}
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                padding: '0.375rem 0.75rem',
                                border: '1px solid var(--border-medium, #cbd5e1)',
                                borderRadius: '6px',
                                background: 'var(--bg-card, #ffffff)',
                                color: 'var(--text-secondary, #64748b)',
                                fontSize: '0.8125rem',
                                cursor: isLoading ? 'not-allowed' : 'pointer',
                                opacity: isLoading ? 0.6 : 1
                            }}
                        >
                            <RefreshCw size={14} style={{ animation: isLoading ? 'spin 1s linear infinite' : 'none' }} />
                            Aktualisieren
                        </button>
                    </div>
                )}

                {/* PDF Content */}
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {isLoading ? (
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '1rem',
                            color: 'var(--text-secondary, #64748b)'
                        }}>
                            <Loader2 size={40} style={{ animation: 'spin 1s linear infinite', color: 'var(--accent-slate, #5b7c8d)' }} />
                            <p style={{ margin: 0 }}>PDF wird generiert...</p>
                        </div>
                    ) : error ? (
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '1rem',
                            color: 'var(--text-secondary, #64748b)',
                            textAlign: 'center',
                            padding: '2rem'
                        }}>
                            <FileText size={48} style={{ opacity: 0.3, color: '#ef4444' }} />
                            <p style={{ margin: 0, color: '#ef4444', fontWeight: 500 }}>
                                Fehler bei der PDF-Generierung
                            </p>
                            <p style={{ margin: 0, fontSize: '0.875rem' }}>{error}</p>
                            <button
                                onClick={generatePdf}
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                    padding: '0.5rem 1rem',
                                    border: '1px solid var(--border-medium, #cbd5e1)',
                                    borderRadius: '6px',
                                    background: 'var(--bg-card, #ffffff)',
                                    color: 'var(--text-primary, #2c3e50)',
                                    fontSize: '0.875rem',
                                    cursor: 'pointer'
                                }}
                            >
                                <RefreshCw size={14} />
                                Erneut versuchen
                            </button>
                        </div>
                    ) : pdfUrl ? (
                        <iframe
                            src={pdfUrl}
                            style={{
                                width: '100%',
                                height: '100%',
                                minHeight: '65vh',
                                border: 'none'
                            }}
                            title="Vertragsvorschau"
                        />
                    ) : (
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '1rem',
                            color: 'var(--text-muted, #94a3b8)'
                        }}>
                            <FileText size={48} style={{ opacity: 0.3 }} />
                            <p style={{ margin: 0 }}>Kein Vertragsinhalt vorhanden</p>
                        </div>
                    )}
                </div>
            </Modal.Body>

            <Modal.Footer style={{
                borderTop: '1px solid var(--border-light, #e2e8f0)',
                padding: '1rem 1.5rem'
            }}>
                <Button
                    variant="outline-secondary"
                    onClick={handleClose}
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        border: '1px solid var(--border-medium, #cbd5e1)',
                        color: 'var(--text-primary, #2c3e50)',
                        background: 'var(--bg-card, #ffffff)'
                    }}
                >
                    <X size={18} />
                    Schließen
                </Button>
            </Modal.Footer>

            {/* CSS for spin animation */}
            <style>{`
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
            `}</style>
        </Modal>
    );
};

export default ContractPreviewModal;
